"use client";
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import theme from '../../styles/theme';

export type ThemeName = 'dark' | 'light';

type ThemeContextValue = {
  theme: ThemeName;
  setTheme: (next: ThemeName) => void;
};

const STORAGE_KEY = 'bp411:theme';

const ThemeContext = createContext<ThemeContextValue>({ theme: 'dark', setTheme: () => {} });

// FIX-48: this app mounts ThemeProvider in two different ways - once
// globally (app/components/Providers.tsx, so the Navbar's ThemeToggle works
// everywhere) and again locally on a handful of pages that predate the
// global mount (app/page.tsx, app/dashboard/page.tsx, app/charities/**,
// app/wallet-socials/**, which only ever used ThemeProvider for its old
// "apply CSS tokens on mount" behavior). Each mount owns its own React
// context, so a toggle click against the global provider would not, on its
// own, update a nested local provider's state. This tiny pub/sub keeps every
// currently-mounted ThemeProvider instance in sync; localStorage keeps them
// in sync across full page loads/navigations.
const listeners = new Set<(next: ThemeName) => void>();

function readStoredTheme(): ThemeName {
  if (typeof window === 'undefined') return 'dark';
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function applyTheme(next: ThemeName) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const tokens = next === 'light' ? theme.lightTokens : theme.darkTokens;
  Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(k, String(v)));
  root.setAttribute('data-theme', next);
}

/** Read the current theme/setTheme pair. Must be used under <ThemeProvider>. */
export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  // FIX: this used to lazy-init from localStorage directly
  // (useState(() => readStoredTheme())). That function runs synchronously
  // during React's *first client render* - which happens during hydration,
  // not after it - so for any visitor with 'light' saved from a previous
  // visit, the client's first render produced mode='light' while the
  // server (no window/localStorage) had rendered assuming 'dark'. That's a
  // real server/client divergence, and the exact kind of thing that trips
  // React's hydration mismatch error. Always starting from the static
  // 'dark' default (matching styles/globals.css and the server render) and
  // only reading localStorage inside an effect - after hydration has
  // committed - keeps the first render identical on both sides. See the
  // blocking script in app/layout.tsx for how the *visual*
  // flash-of-dark-then-light gap on reload is closed independently of this.
  const [mode, setMode] = useState<ThemeName>('dark');

  useEffect(() => {
    const stored = readStoredTheme();
    if (stored !== mode) setMode(stored);
    // Intentionally run only once on mount - this syncs React's belief
    // about the theme with localStorage after hydration; subsequent
    // changes flow through setTheme()/the listeners set instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyTheme(mode);
  }, [mode]);

  useEffect(() => {
    listeners.add(setMode);
    return () => {
      listeners.delete(setMode);
    };
  }, []);

  const setTheme = useCallback((next: ThemeName) => {
    setMode(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore (private browsing / storage disabled) */
    }
    listeners.forEach((fn) => {
      if (fn !== setMode) fn(next);
    });
  }, []);

  return <ThemeContext.Provider value={{ theme: mode, setTheme }}>{children}</ThemeContext.Provider>;
}
